import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { DoczHandle } from "@norkron/web-docz";
import type { SnapshotStats } from "./useEditorSnapshot";
import { CEREBR8_PAGE_BOX } from "./canvasPageMargins";
import { useEditorSnapshot } from "./useEditorSnapshot";
import {
  createDocumentFormatApi,
  type EditorHostBridge,
  type EditorToolbarConfig,
  type ElaborateEditorHandle,
  resolveToolbarConfig,
  type ToolbarEnv,
  ToolbarShell,
} from "@norkron/web-elaborate-editor";

/**
 * `<EditorToolbarMount>` — the elaborate glue that mounts the
 * `@norkron/web-elaborate-editor` v0.5.0 `<ToolbarShell>` (ribbon OR simple menu
 * bar + compact toolbar) over the live document editor, driving it entirely from
 * the workspace's persisted `EditorToolbarConfig`.
 *
 * RULE ZERO: the shell owns NO document logic. This mount binds it to the REAL
 * docz-backed `FormatApi` (`createDocumentFormatApi(handle, module)`) + the editor
 * handle + an `EditorHostBridge` (structural inserts + host actions the editor
 * cannot own) + a live `active`-format snapshot re-read on every selection change.
 * Every toolbar command routes through the SAME `docz.wasm` verbs the editor's
 * keyboard path uses — the toolbar is a pure projection, never a second model.
 *
 * The shell is PORTALED into the pane-header `toolbarContainer` (the same dock the
 * lib's built-in `FormattingToolbar` used), so it renders as header chrome above
 * the paper — never a sticky element inside the scrolling canvas.
 */

export interface EditorToolbarMountProps {
  /** The live editor handle (all docz mark/block verbs + `activeFormat()`). */
  readonly editorHandle: ElaborateEditorHandle;
  /** The docz doc handle — its `.module` presence-probes the `supports.*` gates. */
  readonly doczHandle: DoczHandle;
  /** The workspace's persisted toolbar config (resolved to a render-ready shape). */
  readonly config: EditorToolbarConfig;
  /** Host actions the editor cannot own itself (save/export/print/settings/…). The
   *  structural inserts (insertBlock/insertPageBreak) are wired to the handle here;
   *  genuinely-absent gaps (undo/redo/find) stay undefined → their commands render
   *  DISABLED with an honest tooltip. */
  readonly host: Partial<EditorHostBridge>;
  /** The pane-header dock node to portal the toolbar into (null → not mounted). */
  readonly container: HTMLElement | null;
  /** Bumped by the host on every editor CHANGE. The enable environment is derived
   *  from LIVE probes (`canUndo()` / `canRedo()` / `canPaste()` / `painterArmed`),
   *  and an edit moves those without moving the caret — so `selectionchange` alone
   *  would leave the History buttons stale. Any new value re-derives `env`. */
  readonly revision?: number;
  /** Re-focus the canvas after a command so typing continues from the caret. */
  readonly onAfter?: () => void;
  /** Fired when the live View ▸ mode toggle flips ribbon⇄simple (for host UI sync). */
  readonly onModeChange?: (mode: "ribbon" | "simple") => void;
  /** Test/measurement seam: counts selection events observed, frames sampled and
   *  state commits. Undefined in production — the counters cost nothing when
   *  absent (a single `if` in the rAF callback). */
  readonly perfStats?: SnapshotStats;
}

export function EditorToolbarMount(props: EditorToolbarMountProps) {
  const { editorHandle, doczHandle, config, host, container, onAfter, revision } =
    props;

  // The live surface snapshot — active format + the four enable gates + the live
  // host probes — FRAME-COALESCED and VALUE-GATED (`useEditorSnapshot`). It moves
  // only when something the toolbar can actually render differs, so a drag through
  // plain text (the common case) commits ONCE instead of once per input event.
  //
  // This replaced a raw `selectionchange → setSelTick(n+1)` counter plus a
  // `handle.activeFormat()` call in the RENDER BODY. Measured on the live
  // paginated canvas, one 5.1 s drag: 122 events → 122 renders → 1123 ms of
  // react-dom work, 63% of the whole main thread. See `editorSnapshot.ts`.
  const snapshot = useEditorSnapshot(editorHandle, host, revision, props.perfStats);

  const formatApi = useMemo(
    () => createDocumentFormatApi(editorHandle, doczHandle.module),
    [editorHandle, doczHandle],
  );
  const resolved = useMemo(() => resolveToolbarConfig(config), [config]);

  // The View ▸ mode toggle flips ribbon⇄simple LIVE (no round-trip); the persisted
  // workspace default (resolved.mode) is the initial value and re-seeds on reload.
  const [mode, setMode] = useState<"ribbon" | "simple">(resolved.mode);
  useEffect(() => setMode(resolved.mode), [resolved.mode]);

  const bridge: EditorHostBridge = useMemo(
    () => ({
      insertBlock: (block) => editorHandle.insertBlock(block),
      insertPageBreak: () => editorHandle.insertPageBreak(),
      addComment: host.addComment,
      undo: host.undo,
      redo: host.redo,
      find: host.find,
      // The clipboard + painter buttons invoke the SAME host code paths the native
      // ⌘X/⌘C/⌘V gestures do (the controller's clipboard controller / painter) —
      // never a second implementation. Absent → the command renders disabled below.
      cut: host.cut,
      copy: host.copy,
      paste: host.paste,
      formatPainter: host.formatPainter,
      // The LIVE probes. Passed through so a command's `run` (and any future shell
      // affordance) can consult the real stack depth / browser permission too.
      canUndo: host.canUndo,
      canRedo: host.canRedo,
      canPaste: host.canPaste,
      setMode: (m) => {
        setMode(m);
        host.setMode?.(m);
        props.onModeChange?.(m);
      },
      openSettings: host.openSettings,
      export: host.export,
      print: host.print,
      save: host.save,
    }),
    [editorHandle, host, props.onModeChange],
  );

  // The live active-format snapshot + enable environment. `supports.*` come from
  // the loaded docz.wasm; the host flags light a GAP command only when the host
  // ACTUALLY provides that action (else it degrades to disabled — honest).
  //
  // WHERE THE VALUES COME FROM. `active` and the four gates are read by
  // `useEditorSnapshot` OUTSIDE the render body — reading them here (the old
  // shape) made a wasm call on every render pass and, worse, produced a FRESH
  // object identity on every render, so no memo below could ever hit. Held in
  // state, the identity is stable for as long as the value is, and `<ToolbarShell>`
  // receives byte-identical props across a whole drag.
  //
  // ─── the selection gate comes from the SURFACE, never from the DOM ──────────
  // `window.getSelection()` was the wire behind every `requires: 'selection'`
  // command. On the PAGINATED surface the document is a `<canvas>` plus a hidden
  // input, so that selection is permanently collapsed however much text the
  // reviewer has dragged across — Link rendered permanently disabled on the
  // canvas while the SAME reviewer selection enabled it in continuous mode. Only
  // the surface knows its own selection (the canvas reads the WASM core's
  // `{anchor,focus}`; the DOM controller its cached in-canvas Range), so only the
  // handle may answer. A handle that cannot report degrades to DISABLED — an
  // honest dead control — and NEVER back to the DOM probe that caused the split.
  // (The same reason the snapshot's change-gate compares the PROJECTED value and
  // not a DOM selection range: on the canvas that range never moves.)
  const active = snapshot.active;
  const env: ToolbarEnv = useMemo(
    () => ({
      supports: formatApi.supports,
      hasSelection: snapshot.hasSelection,
      // The caret-in-table gate for the contextual Table ribbon tab. The editor
      // handle owns the caret->table detection; the mount only reads it into
      // `env.inTable` so the row/column/merge/header commands enable when (and
      // only when) the caret is inside a table cell.
      //
      // ⚠ THIS IS THE GATE THE WHOLE TABLE LANE HANGS ON. `CONTEXTUAL_TABS =
      // { table: 'inTable' }` upstream, so `isTabVisible` returns false for the
      // Table TAB whenever this is not exactly `true` — the tab does not render
      // disabled, it does not render AT ALL. The paginated canvas published the
      // literal `false` here until 2026-07-29, which is why placing a caret in a
      // table showed no Table group on the default editing surface.
      inTable: snapshot.inTable,
      hasCellRange: snapshot.hasCellRange,
      // Whether the caret's table HAS a header row — so `table.header-toggle`
      // renders PRESSED and toggles the header OFF as well as on.
      tableHeaderOn: snapshot.tableHeaderOn,
      // The sheet `page.margins` validates against — see canvasEditorServices.
      pageBox: CEREBR8_PAGE_BOX,
      host: {
        comment: Boolean(host.addComment),
        export: Boolean(host.export),
        print: Boolean(host.print),
        settings: Boolean(host.openSettings),
        save: Boolean(host.save),
        // ─── LIVE, not merely PRESENT ──────────────────────────────────────────
        // `Boolean(host.undo)` is a constant: the History buttons could never
        // disable at the ends of the stack, so a click on an empty stack was a
        // silent no-op — the dishonest state this wiring removes. The snapshot
        // carries the host's live probe (falling back to presence only for a host
        // that supplies the action without a probe), re-sampled on every
        // coalesced selection tick AND on every `revision` bump.
        undo: snapshot.undo,
        redo: snapshot.redo,
        // Clipboard WRITE (cut/copy) always works from a user gesture — presence
        // is the honest gate. Clipboard READ from a BUTTON is a browser permission
        // the host alone can probe, so Paste gates on `canPaste()` and NEVER
        // degrades to presence: an unprobed host would otherwise render an enabled
        // button that silently does nothing.
        clipboard: Boolean(host.cut ?? host.copy),
        clipboardRead: snapshot.clipboardRead,
        painter: Boolean(host.formatPainter),
        find: Boolean(host.find),
        // WHOLE-BLOCK deletion (`table.delete`). The canvas routes it through
        // `DoczHandle.deleteBlock` and can always do it; the legacy DOM/palette
        // surface keeps a per-block DTO store with no removal seam and declares
        // nothing, so the command renders honestly disabled there instead of as an
        // enabled button that silently leaves the table on screen and on disk.
        deleteBlock: true,
      },
    }),
    [formatApi, host, snapshot],
  );

  if (!container) return null;
  return createPortal(
    <ToolbarShell
      api={formatApi}
      handle={editorHandle}
      active={active}
      config={resolved}
      mode={mode}
      env={env}
      host={bridge}
      after={onAfter}
    />,
    container,
  );
}
